import React, { useState, useEffect, useRef } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { TrendingUp, TrendingDown, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid
} from 'recharts';

// Live Price Ticker Component
export function LivePriceTicker({ businessId, name, logoUrl, onSelect }) {
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        const res = await api.get(`/sb-market/price/${businessId}`);
        setPriceData(res.data);
      } catch (error) {
        console.error('Failed to fetch price:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPrice();
    const interval = setInterval(fetchPrice, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, [businessId]);
  
  if (loading || !priceData) {
    return (
      <div className="p-3 rounded-lg bg-white/5 animate-pulse">
        <div className="h-12 bg-white/10 rounded" />
      </div>
    );
  }
  
  const isUp = priceData.change_pct >= 0;
  
  return (
    <div 
      className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.02] ${
        isUp ? 'bg-[#00E676]/10 border border-[#00E676]/20' : 'bg-red-500/10 border border-red-500/20'
      }`}
      onClick={() => onSelect && onSelect(businessId)}
      data-testid={`ticker-${businessId}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={logoUrl} alt={name} className="w-10 h-10 rounded-lg object-cover" />
          <div>
            <p className="font-semibold text-white text-sm">{name}</p>
            <p className="text-xs text-gray-500">{businessId.replace('biz_', '').toUpperCase()}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-white">${priceData.price?.toFixed(2)}</p>
          <p className={`text-sm flex items-center gap-1 ${isUp ? 'text-[#00E676]' : 'text-red-500'}`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {isUp ? '+' : ''}{priceData.change_pct?.toFixed(2)}%
          </p>
        </div>
      </div>
    </div>
  );
}

// Live Trading Chart Component
export function LiveTradingChart({ businessId, name, height = 300 }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(24); // hours
  
  useEffect(() => {
    const fetchChart = async () => {
      try {
        const res = await api.get(`/sb-market/chart/${businessId}?hours=${timeframe}`);
        setChartData(res.data);
      } catch (error) {
        console.error('Failed to fetch chart:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChart();
    const interval = setInterval(fetchChart, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [businessId, timeframe]);
  
  const formatTime = (timeStr) => {
    const date = new Date(timeStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };
  
  const formatPrice = (price) => `$${price?.toFixed(2)}`;
  
  // Calculate if chart is trending up
  const isUpTrend = chartData.length >= 2 && 
    chartData[chartData.length - 1]?.close > chartData[0]?.close;
  
  const chartColor = isUpTrend ? '#00E676' : '#FF5252';
  
  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <RefreshCw className="w-6 h-6 text-[#00E676] animate-spin" />
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">{name} Live Chart</h3>
        <div className="flex gap-2">
          {[1, 6, 24, 168].map((h) => (
            <Button
              key={h}
              variant="ghost"
              size="sm"
              className={timeframe === h ? 'bg-white/10 text-white' : 'text-gray-500'}
              onClick={() => setTimeframe(h)}
            >
              {h === 1 ? '1H' : h === 6 ? '6H' : h === 24 ? '1D' : '1W'}
            </Button>
          ))}
        </div>
      </div>
      
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id={`gradient-${businessId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis 
            dataKey="time" 
            tickFormatter={formatTime}
            tick={{ fill: '#888', fontSize: 10 }}
            axisLine={{ stroke: '#333' }}
          />
          <YAxis 
            tickFormatter={formatPrice}
            tick={{ fill: '#888', fontSize: 10 }}
            axisLine={{ stroke: '#333' }}
            domain={['auto', 'auto']}
          />
          <Tooltip
            contentStyle={{ backgroundColor: '#1A1A1A', border: '1px solid #333', borderRadius: 8 }}
            labelStyle={{ color: '#888' }}
            formatter={(value) => [formatPrice(value), 'Price']}
            labelFormatter={formatTime}
          />
          <Area
            type="monotone"
            dataKey="close"
            stroke={chartColor}
            fill={`url(#gradient-${businessId})`}
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// All Prices Board Component
export function LivePricesBoard({ onSelectBusiness }) {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const res = await api.get('/sb-market/prices');
        setPrices(res.data);
      } catch (error) {
        console.error('Failed to fetch prices:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPrices();
    const interval = setInterval(fetchPrices, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);
  
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="p-3 rounded-lg bg-white/5 animate-pulse h-20" />
        ))}
      </div>
    );
  }
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
      {prices.map((price) => (
        <LivePriceTicker
          key={price.business_id}
          businessId={price.business_id}
          name={price.name}
          logoUrl={price.logo_url}
          onSelect={onSelectBusiness}
        />
      ))}
    </div>
  );
}

// Portfolio Positions Component
export function PortfolioPositions() {
  const [positions, setPositions] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const res = await api.get('/sb-market/positions');
        setPositions(res.data);
      } catch (error) {
        console.error('Failed to fetch positions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPositions();
    const interval = setInterval(fetchPositions, 15000); // Update every 15 seconds
    return () => clearInterval(interval);
  }, []);
  
  if (loading) {
    return <div className="animate-pulse h-40 bg-white/5 rounded-lg" />;
  }
  
  if (!positions || positions.positions.length === 0) {
    return (
      <div className="p-6 text-center text-gray-500">
        <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>No positions yet. Start investing to see your portfolio here.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 rounded-lg bg-white/5">
          <p className="text-xs text-gray-500">Total Value</p>
          <p className="text-xl font-bold text-white">${positions.total_value?.toFixed(2)}</p>
        </div>
        <div className={`p-4 rounded-lg ${positions.total_profit >= 0 ? 'bg-[#00E676]/10' : 'bg-red-500/10'}`}>
          <p className="text-xs text-gray-500">Total Profit</p>
          <p className={`text-xl font-bold ${positions.total_profit >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
            {positions.total_profit >= 0 ? '+' : ''}${positions.total_profit?.toFixed(2)}
            <span className="text-sm ml-1">({positions.total_profit_pct?.toFixed(1)}%)</span>
          </p>
        </div>
      </div>
      
      {/* Positions List */}
      <div className="space-y-2">
        {positions.positions.map((pos) => (
          <div 
            key={pos.business_id}
            className="p-3 rounded-lg bg-white/5 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <img src={pos.logo_url} alt={pos.name} className="w-10 h-10 rounded-lg object-cover" />
              <div>
                <p className="font-semibold text-white text-sm">{pos.name}</p>
                <p className="text-xs text-gray-500">
                  {pos.shares?.toFixed(4)} shares @ ${pos.entry_price?.toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="font-bold text-white">${pos.current_value?.toFixed(2)}</p>
                <p className={`text-sm ${pos.profit >= 0 ? 'text-[#00E676]' : 'text-red-500'}`}>
                  {pos.profit >= 0 ? '+' : ''}${pos.profit?.toFixed(2)} ({pos.profit_pct?.toFixed(1)}%)
                </p>
              </div>
              <SellButton businessId={pos.business_id} onSold={() => {
                // Refresh positions after sell
                const fetchPositions = async () => {
                  const res = await api.get('/sb-market/positions');
                  setPositions(res.data);
                };
                fetchPositions();
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sell Button Component
function SellButton({ businessId, onSold }) {
  const [selling, setSelling] = useState(false);
  
  const handleSell = async () => {
    if (!window.confirm('Are you sure you want to sell this position?')) return;
    
    setSelling(true);
    try {
      const res = await api.post(`/sb-market/sell/${businessId}`);
      toast.success(res.data.message || 'Position sold successfully!');
      if (onSold) onSold();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sell');
    } finally {
      setSelling(false);
    }
  };
  
  return (
    <Button
      size="sm"
      variant="outline"
      className="border-[#F7931A]/30 text-[#F7931A] hover:bg-[#F7931A]/10"
      onClick={handleSell}
      disabled={selling}
    >
      {selling ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Sell'}
    </Button>
  );
}

export default { LivePriceTicker, LiveTradingChart, LivePricesBoard, PortfolioPositions };
